import { combineReducers } from 'redux';

import {
  CLEAR_DATA,
  LOAD_DATA_CHUNK,
  HIDE_VISIBLE_ALL,
  HIDE_VISIBLE,
  UNTICK_ALL,
  COPY_TICKED,
  COPY_VISIBLE_ALL,
  MOVE_TICKED,
  MOVE_VISIBLE_ALL,
  CHANGE_TICKED_ARTIST,
  UPDATE_SEARCH_ID,
  UPDATE_PROFILE_STATE,
  UPDATE_DEFAULT_SLOT,
  CLEAR_DEFAULTS,
  UPDATE_SHOW_RECENT_PHOTO,
  UPDATE_MAX_RESULTS,
  SET_SORT,
  UPDATE_SEARCH_MATCHES,
  UPDATE_INCLUDE_INACTIVE,
  CLEAR_RESULTS,
  REMOVE_SEARCH_TERM,
  ADD_SEARCH_TERM,
  SET_MATCHES,
  SET_OFFSET,
  UPDATE_SEARCH_TERM,
  SET_LOADING,
  SET_SEARCH_TERMS,
  SAVE_SEARCH,
  LOAD_SAVED_SEARCHES,
  DELETE_SEARCH,
  LOAD_SEARCH_TERMS,
  RESTORE_HIDDEN,
} from '../actions';

export const initialState = {
  allArtists: [],
  visibleArtists: [],
  invisibleArtists: [],
  copiedArtists: [],
  movedArtists: [],
  matches: undefined,
};

// Main Store Selectors:
export function ssAllArtists(state) {
  return state.searchResults.allArtists;
}

export function ssVisibleArtists(state) {
  return state.searchResults.allArtists.filter((item) =>
    state.searchResults.visibleArtists.includes(item.artist_id),
  );
}

export function ssNumberColumns(state) {
  return state.searchResults.numberColumns;
}

// The centralized reducer:
export function searchResults(state = initialState, action) {
  switch (action.type) {
    case CLEAR_DATA:
      return initialState;
    case CLEAR_RESULTS:
      return { ...state, allArtists: [] };
    case LOAD_DATA_CHUNK:
      return {
        ...state,
        allArtists: [...state.allArtists, ...action.artists],
        visibleArtists: [
          ...state.visibleArtists,
          ...action.artists.map((a) => a.artist_id),
        ],
      };
    case HIDE_VISIBLE_ALL:
      return {
        ...state,
        invisibleArtists: [...state.invisibleArtists, ...state.visibleArtists],
        visibleArtists: [],
      };

    case HIDE_VISIBLE:
      return {
        ...state,
        visibleArtists: [
          ...state.visibleArtists.filter((a) => a !== action.artist.artist_id),
        ],
        invisibleArtists: [...state.invisibleArtists, action.artist.artist_id],
      };
    case MOVE_TICKED:
      return {
        ...state,
        visibleArtists: [
          ...state.visibleArtists.filter(
            (a) => !action.tickedArtists.includes(String(a)),
          ),
        ],
        movedArtists: [...state.movedArtists, ...action.tickedArtists],
      };
    case MOVE_VISIBLE_ALL:
      return {
        ...state,
        movedArtists: state.visibleArtists,
        visibleArtists: [],
      };
    case RESTORE_HIDDEN: {
      return {
        ...state,
        movedArtists: [],
        invisibleArtists: [],
      };
    }

    case COPY_TICKED:
      return {
        ...state,
        copiedArtists: [...state.copiedArtists, ...action.tickedArtists],
      };

    case COPY_VISIBLE_ALL:
      return { ...state, copiedArtists: state.visibleArtists };
    case UPDATE_SEARCH_ID:
      return { ...state, searchId: action.searchId };
    case UPDATE_SEARCH_MATCHES:
      return { ...state, matches: action.matches };
    default:
      return state;
  }
}

export function tickedArtists(state = {}, action) {
  switch (action.type) {
    case MOVE_TICKED:
    case MOVE_VISIBLE_ALL:
    case UNTICK_ALL:
    case CLEAR_DATA:
      return {};
    case CHANGE_TICKED_ARTIST: {
      const artistID = action.artist.artist_id;
      const newState = { ...state };
      newState[artistID] = !state[artistID];

      return newState;
    }
    default:
      return state;
  }
}

// HACK: Load the default states from the DOM
const defaultStates = JSON.parse(
  document.querySelector('#default-profile-states')?.dataset.states ?? '{}',
);
const profileStates = (state = defaultStates, action) => {
  switch (action.type) {
    case UPDATE_PROFILE_STATE: {
      const newState = { ...state };
      newState[action.reference] = action.value;
      return newState;
    }
    case LOAD_SEARCH_TERMS:
      return action.profileStates;
    case CLEAR_DEFAULTS:
      return defaultStates;
    default:
      return state;
  }
};

const initialDefaultsState = {
  tagId: undefined,
  showRecentPhotos: false,
  sort: undefined,
  maxResults: undefined,
  includeInactive: false,
};

const defaults = (state = initialDefaultsState, action) => {
  switch (action.type) {
    case UPDATE_DEFAULT_SLOT:
      return {
        ...state,
        tagId: action.tagId,
      };
    case UPDATE_SHOW_RECENT_PHOTO:
      return { ...state, showRecentPhotos: action.value };
    case CLEAR_DEFAULTS:
      return initialDefaultsState;
    case SET_SORT:
      return { ...state, sort: action.sort };
    case UPDATE_MAX_RESULTS:
      return { ...state, maxResults: action.maxResults };
    case UPDATE_INCLUDE_INACTIVE:
      return { ...state, includeInactive: action.includeInactive };
    case LOAD_SEARCH_TERMS:
      return action.defaults;
    default:
      return state;
  }
};

function terms(state = null, action) {
  switch (action.type) {
    case ADD_SEARCH_TERM:
      return [...state, action.term];
    case REMOVE_SEARCH_TERM:
      return state.filter((t) => t.term_id !== action.termID);
    case UPDATE_SEARCH_TERM: {
      const newState = [...state];
      const index = newState.findIndex((v) => v.term_id === action.termID);
      const term = { ...newState[index], ...action.values };
      newState[index] = term;
      return newState;
    }
    case SET_SEARCH_TERMS:
      return action.terms;
    case LOAD_SEARCH_TERMS:
      return action.terms;
    case CLEAR_DATA:
      return [];
    default:
      return state;
  }
}

function matches(state = null, action) {
  switch (action.type) {
    case SET_MATCHES:
      return action.matches;
    case CLEAR_DATA:
      return null;
    default:
      return state;
  }
}

function offset(state = null, action) {
  switch (action.type) {
    case SET_OFFSET:
      return action.offset;
    case CLEAR_DATA:
      return null;
    default:
      return state;
  }
}

function isLoading(state = false, action) {
  switch (action.type) {
    case SET_LOADING:
      return action.loading;
    default:
      return state;
  }
}

// Dummy reducer to be able to persist the old state in localStorage until the migration is done
function savedSearches(state = {}, action) {
  return state;
}

function userSavedSearches(state = {}, action) {
  switch (action.type) {
    case SAVE_SEARCH:
      return {
        ...state,
        [action.id]: {
          name: action.name,
          terms: action.terms,
          profileStates: action.profileStates,
          defaults: action.defaults,
          search_identifier: action.id,
        },
      };
    case LOAD_SAVED_SEARCHES:
      return { ...action.searches };
    case DELETE_SEARCH: {
      const newState = { ...state };
      delete newState[action.id];
      return newState;
    }
    default:
      return state;
  }
}

const rootReducer = combineReducers({
  searchResults,
  profileStates,
  defaults,
  tickedArtists,
  terms,
  matches,
  offset,
  isLoading,
  savedSearches,
  userSavedSearches,
});

// eslint-disable-next-line import/no-default-export -- FIXME: Convert to a named export https://github.com/wegotpop/popsss/wiki/Imports#default-exports
export default rootReducer;
